import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { cdpClient } from '../client';
import { activeProjectId } from '../projectScope';
import type { PartnerLink, PartnerLinkCreate, PartnerLinkUpdate } from '../types';

function requireProjectId(): string {
  const id = activeProjectId.value;
  if (!id) throw new Error('Выберите проект');
  return id;
}

export const partnerLinksAdapter: CrudAdapter<
  PartnerLink,
  PartnerLinkCreate,
  PartnerLinkUpdate & { partner_key: string }
> = {
  async list({ offset, limit }) {
    const projectId = requireProjectId();
    const { data, error } = await cdpClient.GET('/projects/{project_id}/partner-links', {
      params: { path: { project_id: projectId } },
    });
    if (error) throw error;
    const all = data?.items ?? [];
    return { items: all.slice(offset, offset + limit), total: data?.total ?? all.length };
  },
  async create(payload) {
    const projectId = requireProjectId();
    const { data, error } = await cdpClient.POST('/projects/{project_id}/partner-links', {
      params: { path: { project_id: projectId } },
      body: payload,
    });
    if (error) throw error;
    return { id: data?.id ?? '' };
  },
  async update(payload) {
    const projectId = requireProjectId();
    const row = payload as PartnerLinkUpdate & { id: string; partner_key?: string };
    const partnerKey =
      row.partner_key ?? (await this.byKey?.(row.id))?.partner_key;
    if (!partnerKey) throw new Error('partner_key не найден');
    const { landing_ref_link, app_ref_link } = row;
    const { data, error } = await cdpClient.PATCH(
      '/projects/{project_id}/partner-links/{partner_key}',
      {
        params: { path: { project_id: projectId, partner_key: partnerKey } },
        body: { landing_ref_link, app_ref_link },
      },
    );
    if (error) throw error;
    return (data ?? payload) as PartnerLink;
  },
  async remove(id) {
    const projectId = requireProjectId();
    const row = (await this.list({ offset: 0, limit: 10_000 })).items.find((i) => i.id === id);
    const partnerKey = row?.partner_key ?? String(id);
    const { error } = await cdpClient.DELETE(
      '/projects/{project_id}/partner-links/{partner_key}',
      {
        params: { path: { project_id: projectId, partner_key: partnerKey } },
      },
    );
    if (error) throw error;
  },
  async byKey(id) {
    const items = await this.list({ offset: 0, limit: 10_000 });
    return items.items.find((i) => i.id === id) as PartnerLink;
  },
};

export const partnerLinksStore = useCrudDataSource(partnerLinksAdapter, { keyExpr: 'id' });
